import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, KeyRound, ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Loader, RefreshCw } from 'lucide-react';
import supportAxios from '../../utils/supportAxios';

const SupportVerifyResetCode = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = (searchParams.get('email') || '').trim();

  const [digits, setDigits] = useState(Array(6).fill(''));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const codeValue = digits.join('');

  const inputRefs = React.useRef([]);

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length > 1) {
      return;
    }
    const next = [...digits];
    next[index] = cleaned.slice(-1);
    setDigits(next);
    if (cleaned && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && digits[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill('');
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);
    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async () => {
    setError('');
    if (!email) {
      setError('Missing email. Please request a new verification code.');
      return;
    }
    if (codeValue.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setLoading(true);
    try {
      const res = await supportAxios.post('/support/api/verify-reset-code', {
        email,
        code: codeValue,
      });
      if (res.data.status === 'success') {
        navigate(`/support/reset-password?email=${encodeURIComponent(email)}&code=${encodeURIComponent(codeValue)}`);
      } else {
        setError(res.data.message || 'Invalid verification code.');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to verify code. Please try again.';
      setError(message);
      if (err.response?.data?.expired) {
        setDigits(Array(6).fill(''));
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Missing email.');
      return;
    }
    setResending(true);
    setError('');
    try {
      await supportAxios.post('/support/api/forgot-password', { email });
      setDigits(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-4">Missing Email</h1>
          <Link
            to="/support/forgot-password"
            className="inline-block py-2.5 px-6 bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white rounded-lg font-medium hover:opacity-95 transition"
          >
            Request a New Code
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-semibold text-gray-800">Verify Code</h1>
        </div>

        <p className="text-sm text-gray-600 text-center mb-2">
          Enter the 6-digit code sent to
        </p>
        <p className="text-sm font-medium text-purple-700 text-center mb-6 break-all">{email}</p>

        {error && (
          <div className="mb-4 flex items-center text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={loading}
              className="w-11 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent outline-none disabled:bg-gray-50"
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          disabled={loading || codeValue.length !== 6}
          className="w-full py-2.5 bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white rounded-lg font-medium hover:opacity-95 disabled:opacity-60 transition flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              Verify &amp; Continue
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        <button
          onClick={handleResend}
          disabled={resending}
          className="w-full mt-3 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-60 transition flex items-center justify-center gap-2"
        >
          {resending ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Resending...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Resend Code
            </>
          )}
        </button>

        <div className="mt-4">
          <Link to="/support/login" className="text-sm text-purple-600 hover:underline flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SupportVerifyResetCode;